import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function VoiceChangerPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Voice changer pro"
      date="NOVEMBER 2025"
    >
      <p>
        Developer: Aleksandra Rakitina
        <br />
        Contact (privacy requests): alexwadek@gmail.com
      </p>

      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “Voice changer pro” (the “App”)
        collects, uses, stores, and discloses information about users. It covers
        onboarding and registration, microphone access and audio recording, file
        import from device storage, application of voice effects, real-time
        preview, export to MP3/WAV, sharing to destinations you choose, local
        history of processed files, subscription management, analytics focused
        on reliability, push notifications, and customer support. The App is
        built with a local-first approach so recordings and processed audio can
        remain on your device by default unless you explicitly share or back
        them up using external services of your choice. By using the App, you
        acknowledge this Policy and agree to its terms. If you do not agree,
        uninstall the App.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The data controller that determines the purposes and means of
        processing for the App is Aleksandra Rakitina. You may contact us at
        alexwadek@gmail.com with questions about this Policy or to exercise
        privacy rights such as access, deletion, correction, restriction,
        objection, or data portability. To protect your account and data, we
        will verify your identity before acting on requests, typically by
        confirming control of the email address used within the App or
        providing another minimal verification step. We retain privacy
        correspondence for compliance and security documentation where allowed
        by law. If your request involves platform-level operations such as
        subscription cancellation or refunds, we will direct you to the
        relevant account settings while addressing any privacy-specific issues
        within our scope.
      </p>

      <div className={s.title}>
        3) Key Principles: Local-First, Minimization, User Control
      </div>
      <p>
        The App is designed around practical privacy principles. Local-first
        means audio you record or import can be processed on your device without
        uploading the content to our servers. Minimization means we collect only
        what is necessary to deliver requested features and maintain service
        stability; we avoid collecting audio content for analytics or marketing.
        User control means you choose what to import, how to process it, where
        to export or share it, and when to delete it. Operational metadata
        needed to run features is kept as lightweight as possible, and optional
        components like cloud sync are not required for core functionality. When
        laws or platform policies require consent, we request it transparently
        in the interface.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        For clarity, the following terms apply in this Policy. “Personal Data”
        means any information relating to an identified or identifiable person,
        including account identifiers from authentication and subscription
        systems. “Audio Content” refers to sound recordings and imported audio
        files you select for processing in the App. “Processed Output” means
        audio generated after applying effects such as robot, chipmunk, monster,
        echo, reverb, radio, alien, water/bubble, pitch shift, or tempo changes.
        “Local Metadata” includes on-device records such as filenames, effect
        selections, timestamps, durations, and internal identifiers.
        “Processing” covers collection, storage, transformation, playback,
        export, and deletion. “Service Providers” are third parties acting on
        our instructions to supply authentication, subscriptions, analytics,
        notifications, and crash diagnostics.
      </p>

      <div className={s.title}>5) What We Do Not Collect or Upload</div>
      <p>
        We do not collect or remotely store the content of your Audio Content or
        Processed Output for our own servers. The App does not upload your
        recordings to us for analysis or marketing. We do not use ad SDKs that
        scan audio to create behavior profiles, and we do not sell Personal
        Data. Access to your microphone or files is requested only to perform
        actions you initiate, such as recording or importing audio. If you share
        a file through system share sheets or back it up to a cloud or messaging
        app, those destinations operate under their own privacy policies that we
        do not control. We also do not require precise location, contacts, or
        photos to use the core features.
      </p>

      <div className={s.title}>6) Categories of Data We Process</div>
      <p>
        Depending on your choices and device permissions, we may process these
        categories. Account and sign-in data: email, provider identifiers, and
        display name when available, used to manage access and restore
        purchases. Audio Content and Processed Output: handled locally for
        recording, effects, preview, and export; not uploaded to our servers.
        Local Metadata: filenames, timestamps, effect selections, and counters
        to manage free-tier limits and history capacity. Subscription and
        entitlement data: product identifiers, trial status, renewal outcomes,
        expirations, and cancellations for premium features. Technical and
        diagnostic data: device/OS/app versions, language and time zone,
        anonymized crash reports, and feature usage counts. Notification tokens:
        used solely to route push messages you permit.
      </p>

      <div className={s.title}>7) Sources of Data and Voluntary Provision</div>
      <p>
        Most information comes directly from you when you create an account,
        record audio, import files, apply effects, adjust export settings, and
        manage history. Additional data arises automatically from App operation,
        such as event counters and crash diagnostics that help us improve
        reliability across devices. Providing some data is optional, but certain
        features will not work without it: microphone permission is needed for
        recording; storage permission is needed to import audio; authentication
        is required to link subscriptions and restore purchases; notification
        tokens are required to deliver push messages. You can revoke permissions
        in system settings, sign out, or disable notifications at any time,
        which stops new data flows to those components.
      </p>

      <div className={s.title}>
        8) Microphone, File Access, and Local Processing
      </div>
      <p>
        The App requests microphone permission for recording and storage
        permission for importing audio you explicitly select. Recording and
        preview occur on-device, and temporary buffers exist only as long as
        needed to capture and render audio. Imported files are read only after
        you choose them in system pickers, and the App does not scan unrelated
        folders. By default, you can keep files local within the App’s sandbox
        and export only when you choose. Exported files saved to system or cloud
        destinations leave our control and become subject to the destination’s
        policies. You can delete recordings and processed results in-app to free
        storage and reduce exposure.
      </p>

      <div className={s.title}>
        9) Audio Effects, Real-Time Preview, and Libraries
      </div>
      <p>
        To apply effects, the App uses audio processing libraries that run
        locally. Effects may include pitch shift, formant changes, time stretch,
        reverb, echo, EQ, or band-limiting to simulate radio or other
        aesthetics. Real-time preview streams audio through in-memory pipelines
        to let you hear changes before saving. These pipelines operate on-device
        and do not transmit Audio Content to our servers. Parameter settings you
        adjust (for example, effect intensity or preset selection) may be stored
        as Local Metadata so you can reproduce a sound in future sessions. We do
        not analyze your audio content for advertising, and we avoid attaching
        content snippets to crash reports or analytics.
      </p>

      <div className={s.title}>10) History and Local Storage</div>
      <p>
        The App can maintain a local history of recent recordings and processed
        files so you can revisit, re-export, or share later. In the free tier,
        history may be limited to approximately three saved items; premium plans
        can allow unlimited items. History entries include Local Metadata such
        as filename, duration, and applied effect. Deleting an entry in the App
        removes the stored copy from the App’s sandbox, while exported copies
        you previously sent to other destinations remain under those
        destinations’ control. Uninstalling the App removes the sandbox and its
        contents; export what you want to keep before uninstalling or resetting
        your device.
      </p>

      <div className={s.title}>
        11) Subscriptions, Trials, Pricing, and Entitlements
      </div>
      <p>
        Monetization uses in-app purchases and an entitlement system to manage
        access to premium features. Current plans include a 3-day trial and
        pricing of $4.99 per week or $39.99 per year. The free tier may provide
        up to three effects and storage for up to three files; premium access
        unlocks the full effect set, unlimited saved items, and advanced export
        bitrates. We receive subscription metadata from our billing provider
        such as product identifiers, trial eligibility, renewal results,
        expiration dates, and cancellation status to grant or revoke access. We
        do not receive payment card numbers. Manage or cancel subscriptions in
        your platform account settings; refunds and grace periods follow
        platform rules.
      </p>

      <div className={s.title}>
        12) Export, Sharing, and Third-Party Destinations
      </div>
      <p>
        When you export to MP3 or WAV, the resulting file is saved to a location
        you choose or shared through system share sheets to apps and services
        you select. Those destinations have their own privacy and security
        practices, which we do not control. Deleting a track inside the App does
        not remove copies you previously exported or shared elsewhere. If you
        send content to social platforms, messaging services, drives, or email,
        review their policies before sharing sensitive or private recordings. To
        reduce unintended disclosure on shared devices, consider limiting
        preview content in notifications and regularly clearing the App’s local
        history.
      </p>

      <div className={s.title}>
        13) Push Notifications and In-App Messages
      </div>
      <p>
        With your consent, the App may send push notifications such as tips,
        feature announcements, reminders to try new effects, or service messages
        about subscription status. Delivery requires a device token used solely
        to route messages to your device. You can disable notifications in
        system settings or via in-app controls where available. Some
        service-critical messages permitted by law, like security or billing
        notices, may be delivered even if marketing-style notifications are
        disabled. We avoid including Audio Content or transcriptions in
        notification text. If you uninstall the App, the platform generally
        invalidates your token, though short delays can occur before complete
        deactivation propagates.
      </p>

      <div className={s.title}>
        14) Analytics, Performance, and Crash Diagnostics
      </div>
      <p>
        We use analytics to understand reliability and improve performance.
        Typical events include onboarding completion, record button taps, import
        attempts, effect preview usage, export counts, paywall impressions,
        trial starts, purchases, renewals, cancellations, and anonymized error
        codes. Crash diagnostics help us identify device- or OS-specific issues
        and regressions following updates. Where feasible, analytics are
        aggregated or pseudonymized, and we avoid collecting your Audio Content
        or Processed Output for measurement. You can often reduce analytics via
        system privacy settings, understanding this may limit our ability to
        troubleshoot rare bugs. We do not combine analytics with ad networks for
        targeted advertising.
      </p>

      <div className={s.title}>15) Legal Bases and Purposes (EEA/UK)</div>
      <p>
        For users in the EEA/UK, we rely on several legal bases. Performance of
        a contract covers core features you requested, such as recording,
        applying effects, previewing, exporting, and restoring purchases.
        Legitimate interests include ensuring security, preventing abuse, and
        improving stability and performance, balanced against your rights using
        minimization and user controls. Consent applies to optional components
        where required, like notifications or certain analytics categories.
        Legal obligations cover recordkeeping and responses to lawful requests.
        We do not use Audio Content for third-party advertising and we do not
        sell Personal Data. Where purposes materially change, we will update
        this Policy and, if required, seek additional consent.
      </p>

      <div className={s.title}>16) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational measures to
        protect data from unauthorized access, alteration, disclosure, or loss.
        Examples include HTTPS for relevant communications, app-sandboxed
        storage for audio and metadata, platform-provided key storage for
        sensitive tokens, least-privilege access to operational tools, and
        environment separation for development and production. We avoid storing
        raw Audio Content in logs, and we monitor dependencies for known
        vulnerabilities with reasonable update cycles. No system is perfectly
        secure, but the App’s local-first architecture reduces exposure by
        keeping audio processing on your device unless you choose to export or
        share.
      </p>

      <div className={s.title}>17) Data Retention and Deletion</div>
      <p>
        We retain Personal Data only as long as necessary to provide the
        service, maintain security, comply with legal obligations, and resolve
        disputes. Local recordings, processed files, and history entries persist
        until you delete them in-app or uninstall the App. Subscription and
        entitlement metadata may be kept longer for accounting and fraud
        prevention, generally in pseudonymized or aggregated form. Crash and
        performance logs are retained for trend analysis and then deleted or
        de-identified. Verified requests to delete server-side data we control
        (for example, support emails) are handled within a reasonable time,
        noting that distributed systems and backups can introduce short
        propagation delays.
      </p>

      <div className={s.title}>18) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by applicable law. If you believe a minor has used the App
        without proper authorization, contact alexwadek@gmail.com and we will
        remove information and terminate access where applicable. Parents and
        guardians can use device-level controls to limit microphone access and
        in-app purchases. If we discover that we processed data from a child
        without appropriate consent, we will promptly delete such data
        consistent with legal requirements.
      </p>

      <div className={s.title}>19) User Rights and Choices</div>
      <p>
        Subject to local law, you may have rights to access, correct, update, or
        delete Personal Data; to restrict or object to certain processing; and
        to data portability. Within the App, you control most privacy-sensitive
        operations directly: record, import, delete, rename, export, clear
        history, toggle notifications, and manage effect preferences. For
        account-linked records like subscriptions or verified deletion of
        support correspondence, email alexwadek@gmail.com from your sign-in
        address. We will verify your identity and respond in a reasonable
        timeframe. If we cannot fulfill a request due to legal or technical
        limits, we will explain the reason unless prohibited.
      </p>

      <div className={s.title}>
        20) Free Tier Limits, Abuse Prevention, and Fair Use
      </div>
      <p>
        The free tier may include up to three effects and storage for up to
        three saved files. These limits are enforced using local counters and
        entitlement checks. Attempts to bypass limits, automate excessive
        processing, or otherwise misuse resources can trigger protective
        measures such as temporary lockouts, re-authentication prompts, or
        restriction of certain workflows to preserve reliability and cost
        control. Paid plans remove these caps and unlock higher-quality exports
        such as 320 kbps MP3 or WAV, subject to fair-use principles designed to
        keep latency and device resource consumption reasonable.
      </p>

      <div className={s.title}>
        21) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        As a native mobile app, we do not use browser cookies. SDKs may store
        identifiers that support authentication, subscriptions, analytics,
        notifications, and crash diagnostics. These identifiers function
        similarly to cookies by enabling sessions, entitlement checks, and
        measurement. The App does not rely on advertising identifiers for
        targeted ads. Browser “Do Not Track” signals generally do not apply to
        native apps; nonetheless, we keep SDK usage scoped to necessary purposes
        and avoid combining identifiers across services for unrelated marketing.
        Platform privacy settings may allow you to reset device identifiers or
        limit certain measurements.
      </p>

      <div className={s.title}>22) International Data Transfers</div>
      <p>
        Core audio processing occurs on-device and does not require
        international transfer of your Audio Content to our servers. Limited
        Personal Data may be processed by service providers in other
        jurisdictions for subscriptions, analytics, crash diagnostics, or
        notifications. Where required, we rely on appropriate safeguards such as
        contractual protections and platform mechanisms for cross-border
        transfers. Regardless of location, providers must implement security
        measures consistent with this Policy and applicable law. You may contact
        us to request information about relevant transfer mechanisms and, where
        appropriate, summaries of key contractual terms that do not reveal
        confidential commercial information.
      </p>

      <div className={s.title}>
        23) Accuracy, Support, and Account Management
      </div>
      <p>
        We rely on accurate information for subscription status, contact, and
        support. You can manage most settings directly in the App, including
        export quality, effect choices, history cleanup, and notification
        preferences. Subscription management happens in your platform account
        settings, including cancellations and refunds according to platform
        rules. If you believe subscription records are inaccurate or
        entitlements are misapplied, contact us with details so we can
        coordinate corrections with our billing provider. Some updates may take
        time to propagate across caches and devices, especially after
        reinstalls or restoring purchases.
      </p>

      <div className={s.title}>
        24) Third-Party Links and External Content
      </div>
      <p>
        The App may present optional links to documentation, support pages, or
        account settings. When you follow a link, you leave the App environment
        and the destination’s privacy practices apply. We do not control
        third-party content and are not responsible for their policies. Review
        any requests for additional permissions or personal information
        carefully before proceeding. If a linked resource changes, becomes
        unavailable, or alters its terms, we may update or remove the link in a
        future release without prior notice to maintain safety and consistency
        with this Policy.
      </p>

      <div className={s.title}>25) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur.
        Material changes that affect your rights or how we process Personal Data
        will be highlighted in-app, and where legally required, we will seek
        your consent for new uses. Continued use of the App after changes take
        effect signifies acceptance. We encourage periodic review, especially
        when enabling new features such as additional effects, higher export
        qualities, or new sharing options. If you disagree with updates, you may
        stop using the App and request deletion of account-linked records we
        control, subject to verification.
      </p>

      <div className={s.title}>
        26) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise privacy rights, email alexwadek@gmail.com from the address
        associated with your account. Specify the request type (access,
        deletion, correction, restriction, objection, portability) and include
        relevant context such as your device model and app version. We will
        verify your identity to prevent unauthorized access or deletion and may
        request limited additional information or a confirmation code. Where
        feasible, we will provide data in a portable, machine-readable format.
        We may decline or limit requests where permitted by law, for example if
        fulfilling a request would compromise security, interfere with legal
        obligations, or infringe the rights of others.
      </p>
    </PolicyLayout>
  );
}
